import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';

import { fetchStaticQuery, fetchComboQuery } from 'Redux/ducks/algolia-calls';
import { encodeSlug } from 'Helpers/Slug';
import { getFacet, getFacets } from 'Redux/ducks/facet';

const matchFacets = async (hitsObject) => {
  const hitsFacets = Object.keys(hitsObject.facets || {}).filter(f => f !== 'type');

  const allResults = await Promise.all(hitsFacets.map(
    hitFacet => getFacets(hitFacet)
      .then(result => result.data.map((f, i) => ({ ...f, index: i })))
      .catch(() => []),
  ));

  const matchedFacets = {};

  allResults.forEach((facetResult, i) => {
    const matchedFacet = hitsFacets[i];
    matchedFacets[matchedFacet] = orderBy(uniqBy(facetResult.filter((facetResultObj) => {
      const hitResult = get(hitsObject, `facets.${matchedFacet}`, {});
      facetResultObj.matchedCount = hitResult[facetResultObj.name]; // eslint-disable-line no-param-reassign,max-len
      return !!hitResult[facetResultObj.name];
    }), 'slug'), ['matchedCount'], ['desc']);
  });

  if (matchedFacets.categories) {
    matchedFacets.categories.forEach((item, i) => {
      item.matchedIndex = i;
    });

    const totalCategoriesCount = allResults[hitsFacets.indexOf('categories')].filter(r => r.facet === 'categories').length;

    matchedFacets.categories = sortBy(matchedFacets.categories, (facetResultObj) => {
      const matchedPosition = facetResultObj.matchedIndex / matchedFacets.categories.length;
      const barePosition = facetResultObj.index / totalCategoriesCount;

      return matchedPosition + (1 - barePosition * 1.5);
    });

    matchedFacets.categories = sortBy(matchedFacets.categories,
      facetResultObj => (['genres', 'themes'].includes(facetResultObj.subFacet) ? -1 : 1)); // Demote playstyles, because they are probably not relevant
  }

  return matchedFacets;
};

async function fetchHits(facet, slug, page, type, sort, doFacets = false) {
  const relatedFacets = ['type'];

  if (['genres', 'publishers', 'categories', 'themes'].includes(facet)) {
    relatedFacets.push('franchises');
  }

  if (facet === 'publishers' || facet === 'franchises') {
    relatedFacets.push('categories');
  }

  const hitsObject = await fetchStaticQuery({
    facet,
    value: slug,
    callPage: page - 1,
    facets: relatedFacets,
    type,
    index: sort,
  });

  hitsObject.name = get(hitsObject, `hits[0].${facet}`, [])
    .find(p => encodeSlug(p) === slug) || '';

  if (doFacets) {
    hitsObject.related = await matchFacets(hitsObject);

    hitsObject.displayTypes = ['game', 'dlc'].filter(t => get(hitsObject, `facets.type.${t}`, 0) > 0);
  }

  return hitsObject;
}

async function fetchComboHits(combo, page, sort) {
  const hitsObject = await fetchComboQuery(combo, sort, page - 1, ['type', 'franchises']);

  hitsObject.related = await matchFacets(hitsObject);

  hitsObject.displayTypes = ['game', 'dlc'].filter(t => get(hitsObject, `facets.type.${t}`, 0) > 0);

  return hitsObject;
}

export default async function fetchFacetData(facet, slug) {
  const { response, data } = await getFacet(facet.replace('combo-collections', 'combo'), slug);

  const isCombo = get(data, 'facet', null) === 'combo';

  const fetch = isCombo ?
    (page, type, sort) => fetchComboHits(get(data, 'combo'), page, sort)
    :
    (page, type, sort, doFacets) => fetchHits(get(data, 'facet', facet), slug, page, type, sort, doFacets);

  fetch.slug = slug;

  if (response.status === 200) {
    return { facetData: data, fetch };
  }
  return { facetData: null, fetch };
}
